import { useEffect, useState } from 'react'
import { Search, FileText, Download, Trash2, Edit2, Star, X } from 'lucide-react'
import { api } from '../lib/api'

interface Template {
  id: string
  example_note?: string
  instructions?: string
  name: string
  description?: string
  specialty?: string
  note_type?: string
  is_default?: boolean
  sections?: string[]
}

type TabType = 'library' | 'my-templates'

const NOTE_TYPES = [
  { value: 'office', label: 'Office Visit' },
  { value: 'op_note', label: 'Op Note' },
  { value: 'consult', label: 'Consult' },
  { value: 'inpatient', label: 'Rounding' },
  { value: 'procedure', label: 'Procedure' },
]

export function TemplatesPage() {
  const [activeTab, setActiveTab] = useState<TabType>('library')
  const [starterTemplates, setStarterTemplates] = useState<Template[]>([])
  const [myTemplates, setMyTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters for library
  const [specialtyFilter, setSpecialtyFilter] = useState('')
  const [noteTypeFilter, setNoteTypeFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null)
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null)
  const [creatingNew, setCreatingNew] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editExampleNote, setEditExampleNote] = useState('')
  const [editInstructions, setEditInstructions] = useState('')
  const [editNoteType, setEditNoteType] = useState('op_note')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadTemplates()
  }, [activeTab, specialtyFilter, noteTypeFilter])

  async function loadTemplates() {
    setLoading(true)
    setError(null)
    try {
      if (activeTab === 'library') {
        const templates = await api.listStarterTemplates(
          specialtyFilter || undefined,
          noteTypeFilter || undefined
        )
        setStarterTemplates(templates)
      } else {
        const templates = await api.listMyTemplates()
        setMyTemplates(templates)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  function startEdit(template: Template) {
    setEditingTemplate(template)
    setEditName(template.name)
    setEditDescription(template.description || '')
    setEditExampleNote(template.example_note || '')
    setEditInstructions(template.instructions || '')
  }

  async function saveEdit() {
    if (!editingTemplate) return
    setSaving(true)
    try {
      await api.updateMyTemplate(editingTemplate.id, {
        name: editName.trim(),
        description: editDescription.trim(),
        example_note: editExampleNote.trim() || undefined,
        instructions: editInstructions.trim() || undefined,
      })
      setEditingTemplate(null)
      await loadTemplates()
    } catch (err: any) {
      alert(err.message || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(template: Template) {
    if (!confirm(`Delete "${template.name}"? This cannot be undone.`)) return
    try {
      await api.deleteMyTemplate(template.id)
      setMyTemplates(prev => prev.filter(t => t.id !== template.id))
    } catch (err: any) {
      alert(err.message || 'Failed to delete template')
    }
  }

  function startCreate() {
    setCreatingNew(true)
    setEditingTemplate(null)
    setEditName('')
    setEditDescription('')
    setEditExampleNote('')
    setEditInstructions('')
    setEditNoteType('op_note')
  }

  async function saveNew() {
    if (!editName.trim()) return
    setSaving(true)
    try {
      await api.createCustomTemplate({
        name: editName.trim(),
        description: editDescription.trim(),
        note_type: editNoteType,
        example_note: editExampleNote.trim() || undefined,
        instructions: editInstructions.trim() || undefined,
      })
      setCreatingNew(false)
      setActiveTab('my-templates')
      await loadTemplates()
    } catch (err: any) {
      alert(err.message || 'Failed to create template')
    } finally {
      setSaving(false)
    }
  }

  async function handleImport(templateId: string) {
    try {
      await api.importTemplate(templateId)
      // Refresh my templates if on that tab
      if (activeTab === 'my-templates') {
        await loadTemplates()
      }
      alert('Template imported successfully!')
    } catch (err: any) {
      alert(err.message || 'Failed to import template')
    }
  }

  function filterTemplates(templates: Template[]) {
    if (!searchQuery.trim()) return templates
    const query = searchQuery.toLowerCase()
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.specialty?.toLowerCase().includes(query)
    )
  }

  const displayedTemplates = activeTab === 'library'
    ? filterTemplates(starterTemplates)
    : filterTemplates(myTemplates)

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Templates</h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          Browse the library or manage your custom templates
        </p>
      </div>

      {/* Tabs + Actions header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => setActiveTab('library')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'library'
                ? 'text-brand-400 border-b-2 border-brand-500'
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Library
          </button>
          <button
            onClick={() => setActiveTab('my-templates')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'my-templates'
                ? 'text-brand-400 border-b-2 border-brand-500'
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            My Templates
          </button>
        </div>
        <button
          onClick={startCreate}
          className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap self-start sm:self-center"
        >
          + New Template
        </button>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-3 top-2.5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg pl-10 pr-3 py-2 text-gray-900 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        {/* Filters for Library tab */}
        {activeTab === 'library' && (
          <>
            <select
              value={specialtyFilter}
              onChange={(e) => setSpecialtyFilter(e.target.value)}
              className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">All Specialties</option>
              {[...new Set(starterTemplates.map((t) => t.specialty).filter(Boolean))].sort().map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select
              value={noteTypeFilter}
              onChange={(e) => setNoteTypeFilter(e.target.value)}
              className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">All Note Types</option>
              {NOTE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Loading templates...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4 text-center">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Templates Grid */}
      {!loading && !error && (
        <>
          {displayedTemplates.length === 0 ? (
            <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 text-center">
              <FileText size={48} className="mx-auto text-gray-500 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">No templates found</h3>
              <p className="text-gray-400 dark:text-gray-500">
                {activeTab === 'library'
                  ? 'Try adjusting your filters'
                  : 'Import templates from the Library to get started'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedTemplates.map((template) => (
                <div
                  key={template.id}
                  className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 truncate mb-1">
                        {template.name}
                      </h3>
                      {template.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                          {template.description}
                        </p>
                      )}
                    </div>
                    {activeTab === 'my-templates' && template.is_default && (
                      <Star size={16} className="text-yellow-500 flex-shrink-0 ml-2" />
                    )}
                  </div>

                  {/* Meta */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {template.specialty && (
                      <span className="text-xs bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">
                        {template.specialty}
                      </span>
                    )}
                    {template.note_type && (
                      <span className="text-xs bg-brand-500/20 text-brand-400 px-2 py-0.5 rounded-full">
                        {NOTE_TYPES.find(t => t.value === template.note_type)?.label || template.note_type}
                      </span>
                    )}
                  </div>

                  {/* Sections preview */}
                  {template.sections && template.sections.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                        {template.sections.length} sections
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {template.sections.slice(0, 3).map((section, i) => (
                          <span
                            key={i}
                            className="text-[10px] bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 px-1.5 py-0.5 rounded"
                          >
                            {section}
                          </span>
                        ))}
                        {template.sections.length > 3 && (
                          <span className="text-[10px] text-gray-500 dark:text-gray-600">
                            +{template.sections.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    {activeTab === 'library' ? (
                      <>
                        <button
                          onClick={() => setPreviewTemplate(template)}
                          className="flex-1 flex items-center justify-center gap-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg text-sm transition-colors"
                        >
                          <FileText size={14} />
                          Preview
                        </button>
                        <button
                          onClick={() => handleImport(template.id)}
                          className="flex-1 flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          <Download size={14} />
                          Import
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(template)}
                          className="flex-1 flex items-center justify-center gap-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg text-sm transition-colors"
                          title="Edit template"
                        >
                          <Edit2 size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(template)}
                          className="flex items-center justify-center bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg text-sm transition-colors"
                          title="Delete template"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewTemplate(null)}>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100">{previewTemplate.name}</h3>
                <div className="flex gap-2 mt-1">
                  {previewTemplate.specialty && <span className="text-xs bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">{previewTemplate.specialty}</span>}
                  {previewTemplate.note_type && <span className="text-xs bg-brand-500/20 text-brand-400 px-2 py-0.5 rounded-full">{NOTE_TYPES.find(t => t.value === previewTemplate.note_type)?.label || previewTemplate.note_type}</span>}
                </div>
              </div>
              <button onClick={() => setPreviewTemplate(null)} className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"><X size={20} /></button>
            </div>
            <div className="overflow-y-auto p-4 space-y-4">
              {previewTemplate.description && (
                <div>
                  <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase mb-1">Description</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{previewTemplate.description}</p>
                </div>
              )}
              {previewTemplate.example_note && (
                <div>
                  <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase mb-1">Example Note</p>
                  <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans bg-gray-50 dark:bg-gray-800 rounded-lg p-3 leading-relaxed">{previewTemplate.example_note}</pre>
                </div>
              )}
              {previewTemplate.instructions && (
                <div>
                  <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase mb-1">Agent Instructions</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{previewTemplate.instructions}</p>
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3 flex gap-2">
              <button onClick={() => { handleImport(previewTemplate.id); setPreviewTemplate(null) }} className="flex-1 bg-brand-600 hover:bg-brand-500 text-white py-2 rounded-lg text-sm font-medium">Import Template</button>
              <button onClick={() => setPreviewTemplate(null)} className="px-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 rounded-lg text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create Template Modal — full content editor */}
      {(editingTemplate || creatingNew) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <h3 className="font-medium text-gray-900 dark:text-gray-100">{creatingNew ? 'New Template' : 'Edit Template'}</h3>
              <button onClick={() => { setEditingTemplate(null); setCreatingNew(false) }} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Template Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  autoFocus
                />
              </div>

              {/* Note Type (create only) */}
              {creatingNew && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Note Type</label>
                  <select
                    value={editNoteType}
                    onChange={(e) => setEditNoteType(e.target.value)}
                    className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    {NOTE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={2}
                  className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                />
              </div>

              {/* Example Note */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Example Note
                  <span className="font-normal text-gray-400 dark:text-gray-500 ml-1">— the AI uses this as a reference for style and structure</span>
                </label>
                <textarea
                  value={editExampleNote}
                  onChange={(e) => setEditExampleNote(e.target.value)}
                  rows={10}
                  className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y font-mono leading-relaxed"
                  placeholder="Paste or type an example operative note here..."
                />
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  AI Instructions
                  <span className="font-normal text-gray-400 dark:text-gray-500 ml-1">— special instructions for the AI when generating this type of note</span>
                </label>
                <textarea
                  value={editInstructions}
                  onChange={(e) => setEditInstructions(e.target.value)}
                  rows={4}
                  className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
                  placeholder="E.g., Always include tourniquet time. Use metric measurements. List all implant sizes..."
                />
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3 flex gap-2 shrink-0">
              <button
                onClick={creatingNew ? saveNew : saveEdit}
                disabled={saving || !editName.trim()}
                className="flex-1 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium"
              >
                {saving ? 'Saving...' : creatingNew ? 'Create Template' : 'Save Changes'}
              </button>
              <button
                onClick={() => { setEditingTemplate(null); setCreatingNew(false) }}
                className="px-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 rounded-lg text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
